import React from 'react';
import PropTypes from 'prop-types';
import { LuCircleAlert, LuClipboardList, LuEye, LuFlag, LuFolderKanban } from 'react-icons/lu';
import { getArchivedAssignmentLabel } from '../../../utils/assignmentHistory';
import useSmoText from '../smoText';

const Detail = ({ name, children }) => (
  <div className="tw-min-w-0">
    <dt className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400 dark:tw-text-slate-500">{name}</dt>
    <dd className="tw-mt-0.5 tw-break-words tw-text-xs tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{children}</dd>
  </div>
);

Detail.propTypes = { name: PropTypes.string.isRequired, children: PropTypes.node };

const assignedDate = (project, locale) => project.assignedAt?.toDate?.()?.toLocaleDateString(locale)
  || (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString(locale) : '-');

/** One project assignment of a worker (opened from the worker's row). */
const ProjectCard = ({ worker, project, canRemoveSMO, showCity, incompleteCount, onViewVisits }) => {
  const { label, locale } = useSmoText();
  return (
    <div className={`tw-relative tw-flex tw-flex-col tw-gap-3 tw-rounded-2xl tw-border tw-p-3.5 ${project.isFinished ? 'tw-border-violet-500/30 tw-bg-violet-500/[0.04] dark:tw-border-violet-400/25' : 'tw-border-slate-200/80 tw-bg-white/80 dark:tw-border-white/10 dark:tw-bg-white/[0.03]'}`}>
      <div className="tw-flex tw-items-start tw-gap-2">
        <LuFolderKanban aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-sky-500" />
        <span className="tw-min-w-0 tw-flex-1 tw-break-words tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{project.projectTitle}</span>
        {project.isFinished && (
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-violet-500 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-uppercase tw-text-white">
            <LuFlag aria-hidden="true" className="tw-h-3 tw-w-3" />
            {label('projects.finished')}
          </span>
        )}
        <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${project.status === 'active' ? 'tw-bg-emerald-500/15 tw-text-emerald-800 dark:tw-text-emerald-200' : 'tw-bg-slate-500/10 tw-text-slate-600 dark:tw-text-slate-300'}`}>
          {project.status === 'active' ? label('filters.active') : label('filters.inactive')}
        </span>
      </div>
      <dl className="tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-2">
        <Detail name={label('projects.role')}>{project.role || '-'}</Detail>
        <Detail name={label('projects.center')}>
          {project.centerName || '-'}
          {project.isArchived && (
            <span className="tw-ml-1 tw-inline-block tw-rounded-full tw-bg-amber-400/20 tw-px-1.5 tw-py-px tw-text-[10px] tw-font-semibold tw-text-amber-800 dark:tw-text-amber-200">
              {getArchivedAssignmentLabel(project)}
            </span>
          )}
        </Detail>
        {showCity && <Detail name={label('projects.city')}>{project.centerCity || '-'}</Detail>}
        <Detail name={label('projects.type')}>{project.isPrimer ? 'Primer' : 'Backup'}</Detail>
        <Detail name={label('projects.blinded')}>{project.isBlinded ? 'Blinded' : 'Unblinded'}</Detail>
        {canRemoveSMO && (
          <Detail name={label('projects.rate')}>{project.rate?.type ? `${project.rate.type} - ${project.rate.value || ''}` : '-'}</Detail>
        )}
        <Detail name={label('projects.assignedAt')}>{assignedDate(project, locale)}</Detail>
      </dl>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onViewVisits(worker, project);
        }}
        title={incompleteCount > 0 ? label('projects.incompleteHint', { count: incompleteCount }) : undefined}
        className={`tw-mt-auto tw-inline-flex tw-h-9 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-text-white tw-transition ${incompleteCount > 0 ? 'tw-bg-gradient-to-b tw-from-orange-400 tw-to-orange-600 hover:tw-brightness-105' : 'tw-bg-gradient-to-b tw-from-sky-500 tw-to-blue-600 hover:tw-brightness-105'}`}
      >
        <LuEye aria-hidden="true" className="tw-h-4 tw-w-4" />
        {label('projects.viewVisits')}
        {incompleteCount > 0 && <span className="tw-rounded-full tw-bg-white tw-px-1.5 tw-text-[11px] tw-font-bold tw-text-orange-700">{incompleteCount}</span>}
      </button>
    </div>
  );
};

ProjectCard.propTypes = {
  worker: PropTypes.object.isRequired,
  project: PropTypes.object.isRequired,
  canRemoveSMO: PropTypes.bool,
  showCity: PropTypes.bool,
  incompleteCount: PropTypes.number.isRequired,
  onViewVisits: PropTypes.func.isRequired,
};

const NO_TASKS = { byWorker: {} };

/**
 * Body of an opened worker: their project cards, or — with the
 * "Tamamlanmamış" filter — their unfinished visits and calendar tasks.
 * Bölge Müdürleri Raporları passes no unfinished-visit data and shows the city.
 */
const WorkerDetails = ({ worker, statusFilter = 'all', incompleteTasks = NO_TASKS, incompleteVisitCounts, canRemoveSMO, showCity, onViewVisits }) => {
  const { label, locale } = useSmoText();
  const tasks = incompleteTasks.byWorker[worker.id];
  return (
    // Sits inside a table cell; a legacy global `td { white-space: nowrap }` would otherwise stop long names from wrapping
    <div className="tw-space-y-3 tw-whitespace-normal">
      {/* Show incomplete tasks if filter is set to incomplete */}
      {statusFilter === 'incomplete' && tasks && (
        <div className="tw-space-y-2">
          <h4 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
            <LuClipboardList aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-sky-500" />
            {label('tasks.title', { count: tasks.length })}
          </h4>
          <div className="tw-grid tw-gap-2 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
            {tasks.map((task, taskIdx) => (
              // Görevlerin bir kısmı (takvim işleri) aynı id'yi taşımayabilir
              // eslint-disable-next-line react/no-array-index-key
              <div key={taskIdx} className="tw-rounded-xl tw-border tw-border-slate-200/80 tw-bg-white/80 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                  <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${task.type === 'visit' ? 'tw-bg-sky-500/15 tw-text-sky-800 dark:tw-text-sky-200' : 'tw-bg-violet-500/15 tw-text-violet-800 dark:tw-text-violet-200'}`}>
                    {task.type === 'visit' ? label('tasks.visit') : label('tasks.job')}
                  </span>
                  <span className="tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{task.date ? new Date(task.date).toLocaleDateString(locale) : '-'}</span>
                </div>
                <div className="tw-mt-2 tw-text-sm">
                  {task.type === 'visit' ? (
                    <>
                      <strong className="tw-block tw-text-slate-900 dark:tw-text-white">{task.patientId || label('tasks.patient')}</strong>
                      <small className="tw-text-slate-500 dark:tw-text-slate-400">{task.visitType === 'home' ? label('tasks.homeVisit') : label('tasks.siteVisit')}</small>
                    </>
                  ) : (
                    <>
                      <strong className="tw-block tw-text-slate-900 dark:tw-text-white">{task.title}</strong>
                      {task.description && <small className="tw-text-slate-500 dark:tw-text-slate-400">{task.description}</small>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show projects if not filtering by incomplete tasks */}
      {statusFilter !== 'incomplete' && (
        <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-3 2xl:tw-grid-cols-4">
          {worker.projects.map((project, idx) => (
            <ProjectCard
              // Aynı proje birden fazla atama satırıyla gelebilir
              // eslint-disable-next-line react/no-array-index-key
              key={`${project.projectId}-${idx}`}
              worker={worker}
              project={project}
              canRemoveSMO={canRemoveSMO}
              showCity={showCity}
              incompleteCount={incompleteVisitCounts?.byWorkerProject[`${worker.id}|${project.projectId}`] || 0}
              onViewVisits={onViewVisits}
            />
          ))}
        </div>
      )}
      {statusFilter !== 'incomplete' && incompleteVisitCounts && (
        <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
          <LuCircleAlert aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-orange-500" />
          {label('projects.help')}
        </p>
      )}
    </div>
  );
};

WorkerDetails.propTypes = {
  worker: PropTypes.object.isRequired,
  statusFilter: PropTypes.string,
  incompleteTasks: PropTypes.object,
  incompleteVisitCounts: PropTypes.object,
  canRemoveSMO: PropTypes.bool,
  showCity: PropTypes.bool,
  onViewVisits: PropTypes.func.isRequired,
};

export default WorkerDetails;
